// FirewallOverview.tsx
import { useEffect, useState } from "react";
import {
    ShieldAlert, Server, Key,
    Cpu, Network, Users, Lock, Shield,
    CheckCircle2, XCircle, Loader2,
    Radio, FileKey, HardDrive, Clock, Router, Wifi,
    MapPinCheckInside,
} from "lucide-react";
import { showError } from "../../../../utils/toast";
import baseapi from "../../../../services/baseapi";
import { useLocation, useNavigate } from "react-router-dom";

// ── Interfaces ────────────────────────────────────────────────────────────────
interface FirewallDetail {
    id: number; asset_id: number; firewall_type: string; hostname: string;
    os_version: string; os_build: number; serial_number: string; model: string;
    model_number: string; vendor: string; os_full_name: string; management_ip: string;
    timezone: string; admin_timeout: number; dns_primary: string; dns_secondary: string;
    ntp_enabled: boolean; ssl_vpn_enabled: boolean; ssl_vpn_port: number; updated: string;
    device_info: string; pre_auth_tls: string; cpu_memory: string; high_availability: string;
    syslog_servers: string; tls_info: string; compliance_summary: string; summary: string;
    overview: string; hardware: string;
}
interface Hardware {
    cpu: {
        idle_pct: number; user_pct: number; core_count: number;
        system_pct: number; current_pct: number;
        "1min_avg_pct": number; "1min_max_pct": number; "1min_min_pct": number;
    };
    disk: { partitions: any[] };
    memory: { used_mb: number; total_mb: number; used_pct: number };
}
interface HA { mode: string; peers: any[]; enabled: boolean; group_name: string; peer_count: number; password_set: boolean; }
interface PreAuthTLS { port: number; tls_cipher: string; tls_version: string; tls_fingerprint_hash: string; }
interface SyslogServer { mode: string; port: number; format: string; server: string; status: string; facility: string; }
interface ComplianceSummary { info: number; score: number; total: number; errors: number; compliant: number; non_compliant: number; }
interface Summary {
    ha_enabled: boolean; total_users: number; total_zones: number; up_interfaces: number;
    active_sessions: number; total_nat_rules: number; total_interfaces: number;
    total_vpn_tunnels: number; active_vpn_tunnels: number; ssl_vpn_active_users: number;
    total_address_groups: number; total_admin_accounts: number; total_security_rules: number;
    total_address_objects: number; enabled_security_rules: number;
}
interface NetworkOverview {
    wan_ip: string; gateway: string; tls_error: string; tls_cipher: string;
    dns_primary: string; tls_cert_cn: string; tls_version: string;
    wan_rx_mbps: number; wan_tx_mbps: number; lan1_rx_mbps: number; lan1_tx_mbps: number;
    wan_interface: string; lan1_interface: string; active_sessions: number;
    tls_expiry_days: number | null; tls_self_signed: boolean;
    wan_rx_mb_total: number; wan_tx_mb_total: number;
    lan1_rx_mb_total: number; lan1_tx_mb_total: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseJson = <T,>(raw: any, fallback: T): T => {
    if (typeof raw === "object" && raw !== null) {
        return raw as T;
    }
    try { return JSON.parse(raw) as T; } catch { return fallback; }
};

const getPctColor = (pct: number) => {
    if (pct >= 90) return { ring: "#ef4444", bar: "#ef4444", text: "text-red-400" };
    if (pct >= 70) return { ring: "#f97316", bar: "#f97316", text: "text-orange-400" };
    if (pct >= 50) return { ring: "#eab308", bar: "#eab308", text: "text-yellow-400" };
    return { ring: "#22c55e", bar: "#22c55e", text: "text-green-400" };
};

// ── Gauge Ring ────────────────────────────────────────────────────────────────
const GaugeRing = ({ pct, size = 96, stroke = 8, color }: {
    pct: number; size?: number; stroke?: number; color: string;
}) => {
    const r = (size - stroke * 2) / 2;
    const circ = 2 * Math.PI * r;
    const cx = size / 2;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
            <circle cx={cx} cy={cx} r={r} fill="none" stroke="#374151" strokeWidth={stroke} />
            <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={stroke}
                strokeDasharray={`${(pct / 100) * circ} ${circ}`} strokeLinecap="round"
                style={{ transition: "stroke-dasharray 0.8s ease" }} />
        </svg>
    );
};

// ── Stat Pill ─────────────────────────────────────────────────────────────────
const StatPill = ({ label, value, color }: { label: string; value: any; color: string }) => (
    <div className="flex flex-col items-center gap-0.5 bg-slate-700/30 border border-slate-600/30 rounded-lg px-2 py-1.5">
        <span className="text-[7px] font-bold text-gray-200 uppercase tracking-widest">{label}</span>
        <span className={`text-[11px] font-black ${color}`}>{value}</span>
    </div>
);

// ── Progress Bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, color }: { pct: number; color: string }) => (
    <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </div>
);

// ── Inline Bar ────────────────────────────────────────────────────────────────
const InlineBar = ({ value, max, color }: { value: number; max: number; color: string }) => (
    <div className="w-full h-1.5 bg-slate-700/60 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min((value / Math.max(max, 1)) * 100, 100)}%`, background: color }} />
    </div>
);

// ── Shared Primitives ─────────────────────────────────────────────────────────
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">{label}</span>
        <div className="text-[11px]">{children}</div>
    </div>
);

const Val = ({ v }: { v: any }) => (
    <span className={!v || v === "-" ? "text-gray-200" : "text-gray-200"}>
        {!v || v === "-" ? "—" : String(v)}
    </span>
);

const BoolPill = ({ value, trueLabel = "Enabled", falseLabel = "Disabled" }: {
    value: boolean; trueLabel?: string; falseLabel?: string;
}) => (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${value ? "text-green-400" : "text-gray-200"}`}>
        {value ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
        {value ? trueLabel : falseLabel}
    </span>
);

const CardHeader = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
    <>
        <div className="flex items-center gap-2 mb-3">
            <div className="bg-slate-800 p-1.5 rounded-lg shrink-0">{icon}</div>
            <h2 className="text-sm font-bold text-white">{label}</h2>
        </div>
        <hr className="border-slate-700 mb-4" />
    </>
);

const SubLabel = ({ label }: { label: string }) => (
    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">{label}</p>
);

// ── Main Component ────────────────────────────────────────────────────────────
interface FirewallOverviewProps { asset_id: string; client_id: string; onDataLoaded?: (hasData: boolean) => void; }

export default function FirewallOverview({ asset_id, client_id, onDataLoaded }: FirewallOverviewProps) {
    const [data, setData] = useState<FirewallDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const goToTab = (tab: string) => {
        const params = new URLSearchParams(location.search);
        params.set("tab", tab);

        navigate({
            pathname: location.pathname,   // keeps current route
            search: params.toString(),     // updates only query params
        });
    };

    useEffect(() => {
        if (!asset_id) return;
        (async () => {
            setLoading(true);
            try {
                const res = await baseapi.get("/firewall_asset_details", {
                    params: { query: JSON.stringify({ asset_id: parseInt(asset_id), client_id: parseInt(client_id) }) }
                });
                if (res.data.status) {
                    if (res.data.data.length > 0) {
                        setData(res.data.data?.[0] ?? res.data.data);
                    }
                    else {
                        onDataLoaded?.(true);
                    }

                }
                else showError("Failed to load firewall details");
            } catch (e: any) { showError(e.message); }
            finally { setLoading(false); }
        })();
    }, [asset_id]);

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-red-400" />
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center justify-center h-64 gap-2 opacity-40">
            <ShieldAlert className="w-10 h-10 text-red-400" />
            <p className="text-sm text-gray-200 font-bold uppercase tracking-widest">No data found</p>
        </div>
    );

    // ── Parse JSON fields ─────────────────────────────────────────────────────
    const hardware = parseJson<Hardware>(data.hardware, {
        cpu: { idle_pct: 0, user_pct: 0, core_count: 0, system_pct: 0, current_pct: 0, "1min_avg_pct": 0, "1min_max_pct": 0, "1min_min_pct": 0 },
        disk: { partitions: [] },
        memory: { used_mb: 0, total_mb: 0, used_pct: 0 },
    });
    const ha = parseJson<HA>(data.high_availability, { mode: "-", enabled: false, group_name: "", peer_count: 0, password_set: false, peers: [] });
    const preTls = parseJson<PreAuthTLS>(data.pre_auth_tls, { port: 0, tls_cipher: "-", tls_version: "-", tls_fingerprint_hash: "-" });
    const syslog = parseJson<SyslogServer>(data.syslog_servers, { mode: "-", port: 0, format: "-", server: "-", status: "-", facility: "-" });
    const comp = parseJson<ComplianceSummary>(data.compliance_summary, { info: 0, score: 0, total: 0, errors: 0, compliant: 0, non_compliant: 0 });
    const summary = parseJson<Summary>(data.summary, {} as Summary);
    const overview = parseJson<NetworkOverview>(data.overview, {} as NetworkOverview);
    const device_info = parseJson<any>(data.device_info, {});

    const cpuColor = getPctColor(hardware.cpu.current_pct);
    const memColor = getPctColor(hardware.memory.used_pct);
    const compliancePct = comp.total > 0 ? Math.round((comp.compliant / comp.total) * 100) : 0;
    const formatUptime = (seconds?: number) => {
        if (!seconds && seconds !== 0) return "-";

        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const statRows = [
        [
            {
                label: "Interfaces",
                active: summary.up_interfaces,
                total: summary.total_interfaces,
                icon: Network,
                color: "text-blue-400",
                bg: "bg-blue-500/10",
                border: "border-blue-500/20",
                tab: "interface",
            },
            {
                label: "Firewall Rules",
                active: summary.enabled_security_rules,
                total: summary.total_security_rules,
                icon: Shield,
                color: "text-red-400",
                bg: "bg-red-500/10",
                border: "border-red-500/20",
                tab: "rules",
            },
            {
                label: "VPN Tunnels",
                active: summary.active_vpn_tunnels,
                total: summary.total_vpn_tunnels,
                icon: Lock,
                color: "text-purple-400",
                bg: "bg-purple-500/10",
                border: "border-purple-500/20",
                tab: "vpn",
            },
            {
                label: "Active Sessions",
                active: summary.active_sessions,
                icon: Wifi,
                color: "text-sky-400",
                bg: "bg-sky-500/10",
                border: "border-sky-500/20",
            },
        ],
        [
            {
                label: "Uptime",
                active: device_info?.uptime_str ?? formatUptime(device_info?.uptime),
                icon: Clock,
                color: "text-cyan-400",
                bg: "bg-cyan-500/10",
                border: "border-cyan-500/20",
            },
            {
                label: "NAT Rules",
                active: summary.total_nat_rules,
                icon: Router,
                color: "text-orange-400",
                bg: "bg-orange-500/10",
                border: "border-orange-500/20",
            },
            {
                label: "Users",
                active: summary.total_users,
                icon: Users,
                color: "text-green-400",
                bg: "bg-green-500/10",
                border: "border-green-500/20",
                tab: "users",
            }, {
                label: "Zones",
                active: summary.total_zones,
                icon: MapPinCheckInside,
                color: "text-yellow-400",
                bg: "bg-yellow-500/10",
                border: "border-yellow-500/20",

            },

        ],
    ] as const;

    return (
        <div className="space-y-2 animate-fade-in">

            {/* ── Row 0: Stat Ribbon ────────────────────────────────────── */}
            <div className="space-y-2">
                {statRows.map((row, ri) => (
                    <div key={ri} className={`grid gap-2 ${ri === 0 ? "grid-cols-4" : "grid-cols-4"}`}>
                        {row.map((s: any, i) => (
                            <div key={i} onClick={() => s.tab && goToTab(s.tab)} className={`bg-slate-800 border ${s.border} rounded-xl px-4 py-3 flex items-center gap-3 hover:brightness-110 transition`}>
                                <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                                    <s.icon className={`w-4 h-4 ${s.color}`} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest truncate">{s.label}</p>
                                    <p className="text-base font-black text-white leading-tight">
                                        {"total" in s
                                            ? <><span className={s.color}>{s.active}</span><span className="text-gray-200 text-[11px] font-bold"> / {s.total}</span></>
                                            : <span>{s.active ?? "—"}</span>
                                        }
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* ── Row 1: Device Info + Network Overview + Compliance ────── */}
            <div className="grid grid-cols-12 gap-2 items-start">

                {/* Device Info — 5 cols */}
                <div className="col-span-12 lg:col-span-5 bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <CardHeader icon={<Server className="w-4 h-4 text-red-400" />} label="Device Info" />
                    <div className="grid grid-cols-2 gap-x-5 gap-y-3.5">
                        {([
                            { label: "Hostname", value: data.hostname },
                            { label: "Vendor", value: data.vendor },
                            { label: "Model", value: `${data.model} ${data.model_number}` },
                            { label: "Firewall Type", value: data.firewall_type },
                            { label: "OS", value: data.os_full_name },
                            { label: "OS Version", value: data.os_version },
                            { label: "OS Build", value: String(data.os_build) },
                            { label: "Serial No.", value: data.serial_number, mono: true },
                            { label: "Mgmt IP", value: data.management_ip, mono: true },
                            { label: "Timezone", value: data.timezone },
                            { label: "Admin Timeout", value: `${data.admin_timeout} min` },
                            { label: "DNS Primary", value: data.dns_primary, mono: true },
                            { label: "DNS Secondary", value: data.dns_secondary, mono: true },
                        ] as { label: string; value: string; mono?: boolean }[]).map(({ label, value, mono }) => (
                            <Field key={label} label={label}>
                                <span className={`truncate block ${mono ? "font-mono text-gray-200" : "text-gray-200"}`} title={value}>
                                    {value || "—"}
                                </span>
                            </Field>
                        ))}
                        <Field label="NTP"><BoolPill value={data.ntp_enabled} /></Field>
                        <Field label="SSL VPN">
                            {data.ssl_vpn_enabled
                                ? <span className="text-green-400 font-bold">Enabled · {data.ssl_vpn_port}</span>
                                : <span className="text-gray-200">Disabled</span>}
                        </Field>
                    </div>
                </div>

                {/* Network Overview — 4 cols */}
                <div className="col-span-12 lg:col-span-4 bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <CardHeader icon={<Network className="w-4 h-4 text-sky-400" />} label="Network Overview" />
                    <div className="grid grid-cols-2 gap-x-5 gap-y-3.5">
                        {([
                            { label: "WAN IP", value: overview.wan_ip, mono: true },
                            { label: "Gateway", value: overview.gateway, mono: true },
                            { label: "DNS Primary", value: overview.dns_primary, mono: true },
                            { label: "WAN Iface", value: overview.wan_interface },
                            { label: "LAN Iface", value: overview.lan1_interface },
                            { label: "Sessions", value: String(overview.active_sessions ?? "—") },
                            { label: "TLS Version", value: overview.tls_version },
                            { label: "TLS Cipher", value: overview.tls_cipher, mono: true },
                            { label: "Self-Signed", value: overview.tls_self_signed ? "Yes" : "No" },
                            { label: "Expiry Days", value: overview.tls_expiry_days != null ? String(overview.tls_expiry_days) : "—" },
                        ] as { label: string; value: string; mono?: boolean }[]).map(({ label, value, mono }) => (
                            <Field key={label} label={label}>
                                <span className={`truncate block ${mono ? "font-mono text-gray-200" : "text-gray-200"}`} title={value}>
                                    {value || "—"}
                                </span>
                            </Field>
                        ))}
                    </div>

                    {/* Throughput */}
                    <div className="mt-4 space-y-1">
                        <p className="text-[9px] font-bold text-gray-200/50 uppercase tracking-widest">Throughput</p>

                        {/* WAN */}
                        <div className="bg-slate-700/30 border border-slate-700/60 rounded-lg px-3 py-1 space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">WAN ({overview.wan_interface})</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-sky-400">↓ {overview.wan_rx_mbps?.toFixed(1)} Mbps</span>
                                    <span className="text-[9px] font-bold text-violet-400">↑ {overview.wan_tx_mbps?.toFixed(1)} Mbps</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-[8px] text-gray-200">RX Total</span>
                                        <span className="text-[8px] text-sky-400 font-mono">{overview.wan_rx_mb_total != null ? (overview.wan_rx_mb_total / 1024).toFixed(1) + " GB" : "—"}</span>
                                    </div>
                                    <InlineBar value={overview.wan_rx_mbps ?? 0} max={Math.max(overview.wan_rx_mbps ?? 1, overview.wan_tx_mbps ?? 1, 1)} color="#38bdf8" />
                                </div>
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-[8px] text-gray-200">TX Total</span>
                                        <span className="text-[8px] text-violet-400 font-mono">{overview.wan_tx_mb_total != null ? (overview.wan_tx_mb_total / 1024).toFixed(1) + " GB" : "—"}</span>
                                    </div>
                                    <InlineBar value={overview.wan_tx_mbps ?? 0} max={Math.max(overview.wan_rx_mbps ?? 1, overview.wan_tx_mbps ?? 1, 1)} color="#a78bfa" />
                                </div>
                            </div>
                        </div>

                        {/* LAN */}
                        <div className="bg-slate-700/30 border border-slate-700/60 rounded-lg px-3 py-1 space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">LAN ({overview.lan1_interface})</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-emerald-400">↓ {overview.lan1_rx_mbps?.toFixed(1)} Mbps</span>
                                    <span className="text-[9px] font-bold text-orange-400">↑ {overview.lan1_tx_mbps?.toFixed(1)} Mbps</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-[8px] text-gray-200">RX Total</span>
                                        <span className="text-[8px] text-emerald-400 font-mono">{overview.lan1_rx_mb_total != null ? (overview.lan1_rx_mb_total / 1024).toFixed(1) + " GB" : "—"}</span>
                                    </div>
                                    <InlineBar value={overview.lan1_rx_mbps ?? 0} max={Math.max(overview.lan1_rx_mbps ?? 0.1, overview.lan1_tx_mbps ?? 0.1, 0.1)} color="#34d399" />
                                </div>
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-[8px] text-gray-200">TX Total</span>
                                        <span className="text-[8px] text-orange-400 font-mono">{overview.lan1_tx_mb_total != null ? (overview.lan1_tx_mb_total / 1024).toFixed(1) + " GB" : "—"}</span>
                                    </div>
                                    <InlineBar value={overview.lan1_tx_mbps ?? 0} max={Math.max(overview.lan1_rx_mbps ?? 0.1, overview.lan1_tx_mbps ?? 0.1, 0.1)} color="#fb923c" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Compliance — 3 cols */}
                <div className="col-span-12 lg:col-span-3 bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <CardHeader icon={<Shield className="w-4 h-4 text-green-400" />} label="Compliance" />
                    <div className="flex flex-col items-center gap-1">
                        <div className="relative" style={{ width: 140, height: 140 }}>
                            <svg width="140" height="140" viewBox="0 0 180 180" className="-rotate-90">
                                <circle cx="90" cy="90" r="70" fill="none" stroke="#484f5aff" strokeWidth="10" />
                                <circle cx="90" cy="90" r="70" fill="none"
                                    stroke={comp.score >= 70 ? "#22c55e" : comp.score >= 40 ? "#f97316" : "#ef4444"}
                                    strokeWidth="10"
                                    strokeDasharray={`${(comp.score / 100) * 2 * Math.PI * 70} ${2 * Math.PI * 70}`}
                                    strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-black text-white leading-none">{comp.score}</span>
                                <span className="text-[9px] text-gray-200 font-bold uppercase tracking-widest mt-1">Score</span>
                            </div>
                        </div>
                        <div className="w-full space-y-1.5">
                            {([
                                { label: "Total", value: comp.total, color: "text-gray-200", dot: "#64748b" },
                                { label: "Compliant", value: comp.compliant, color: "text-green-400", dot: "#22c55e" },
                                { label: "Non-Compliant", value: comp.non_compliant, color: "text-red-400", dot: "#ef4444" },
                                { label: "Errors", value: comp.errors, color: "text-orange-400", dot: "#f97316" },
                                { label: "Info", value: comp.info, color: "text-blue-400", dot: "#3b82f6" },
                            ]).map(({ label, value, color, dot }) => (
                                <div key={label} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700/40">
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dot }} />
                                        <span className="text-[10px] font-bold text-gray-200 uppercase tracking-widest">{label}</span>
                                    </div>
                                    <span className={`text-[11px] font-black ${color}`}>{value}</span>
                                </div>
                            ))}
                            <div className="pt-1">
                                <div className="flex justify-between mb-1">
                                    <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">Rate</span>
                                    <span className="text-[10px] font-bold text-green-400">{compliancePct}%</span>
                                </div>
                                <InlineBar value={comp.compliant} max={comp.total} color="#22c55e" />
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* ── Row 2: Hardware (full width, 3 cols inside) ───────────── */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <CardHeader icon={<Cpu className="w-4 h-4 text-orange-400" />} label="Hardware" />

                <div className="grid grid-cols-12 gap-4">

                    {/* CPU — 6 cols */}
                    <div className="col-span-12 lg:col-span-6 bg-slate-700/30 border border-slate-700/60 rounded-xl p-3">
                        <SubLabel label="CPU" />
                        <div className="flex items-center gap-5">
                            {/* Gauge */}
                            <div className="relative shrink-0">
                                <GaugeRing pct={hardware.cpu.current_pct} size={88} stroke={7} color={cpuColor.ring} />
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className={`text-[16px] font-black leading-none ${cpuColor.text}`}>
                                        {hardware.cpu.current_pct}%
                                    </span>
                                    <span className="text-[7px] text-gray-200 uppercase tracking-widest font-bold mt-0.5">Live</span>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="flex-1 space-y-2.5">
                                <div className="grid grid-cols-3 gap-1.5">
                                    <StatPill label="Cores" value={hardware.cpu.core_count} color="text-blue-300" />
                                    <StatPill label="User" value={`${hardware.cpu.user_pct}%`} color="text-purple-300" />
                                    <StatPill label="System" value={`${hardware.cpu.system_pct}%`} color="text-cyan-300" />
                                    <StatPill label="Idle" value={`${hardware.cpu.idle_pct}%`} color="text-green-300" />
                                    <StatPill label="1m Avg" value={`${hardware.cpu["1min_avg_pct"]}%`} color="text-yellow-300" />
                                    <StatPill label="1m Max" value={`${hardware.cpu["1min_max_pct"]}%`} color="text-orange-300" />
                                </div>

                                {/* 1-min range */}
                                <div className="space-y-1">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[7px] font-bold text-gray-200 uppercase tracking-widest">1-Min Range</span>
                                        <span className="text-[8px] text-gray-200 font-mono">
                                            {hardware.cpu["1min_min_pct"]}% – {hardware.cpu["1min_max_pct"]}%
                                        </span>
                                    </div>
                                    <div className="relative h-1.5 bg-slate-600/50 rounded-full overflow-hidden">
                                        <div className="absolute h-full rounded-full"
                                            style={{
                                                left: `${hardware.cpu["1min_min_pct"]}%`,
                                                width: `${hardware.cpu["1min_max_pct"] - hardware.cpu["1min_min_pct"]}%`,
                                                background: cpuColor.ring,
                                            }} />
                                        <div className="absolute top-0 h-full w-0.5 rounded-full"
                                            style={{ left: `${hardware.cpu["1min_avg_pct"]}%`, background: cpuColor.ring }} />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[7px] text-gray-200">Min</span>
                                        <span className="text-[7px] text-gray-200">Avg: {hardware.cpu["1min_avg_pct"]}%</span>
                                        <span className="text-[7px] text-gray-200">Max</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stacked breakdown */}
                        <div className="mt-3 space-y-1">
                            <div className="flex justify-between text-[7px] font-bold text-gray-200 uppercase tracking-widest">
                                <span>Usage Breakdown</span>
                                <span>{hardware.cpu.user_pct + hardware.cpu.system_pct}% active</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden flex">
                                <div className="h-full bg-purple-500/70 transition-all duration-700" style={{ width: `${hardware.cpu.user_pct}%` }} />
                                <div className="h-full bg-cyan-500/70 transition-all duration-700" style={{ width: `${hardware.cpu.system_pct}%` }} />
                                <div className="h-full bg-slate-600/30 transition-all duration-700" style={{ width: `${hardware.cpu.idle_pct}%` }} />
                            </div>
                            <div className="flex gap-3">
                                {[
                                    { label: "User", color: "bg-purple-500" },
                                    { label: "System", color: "bg-cyan-500" },
                                    { label: "Idle", color: "bg-slate-600" },
                                ].map(({ label, color }) => (
                                    <div key={label} className="flex items-center gap-1">
                                        <div className={`w-2 h-2 rounded-sm ${color}`} />
                                        <span className="text-[8px] text-gray-200">{label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Memory — 3 cols */}
                    <div className="col-span-12 lg:col-span-3 bg-slate-700/30 border border-slate-700/60 rounded-xl p-3">
                        <SubLabel label="Memory" />
                        <div className="flex flex-col items-center gap-3">
                            <div className="relative">
                                <GaugeRing pct={hardware.memory.used_pct} size={90} stroke={8} color={memColor.ring} />
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className={`text-[18px] font-black leading-none ${memColor.text}`}>
                                        {hardware.memory.used_pct}%
                                    </span>
                                    <span className="text-[7px] text-gray-200 uppercase tracking-widest font-bold mt-0.5">Used</span>
                                </div>
                            </div>
                            <div className="w-full space-y-2">
                                <div className="grid grid-cols-2 gap-1.5">
                                    <StatPill label="Used" value={`${hardware.memory.used_mb.toFixed(0)} MB`} color="text-purple-300" />
                                    <StatPill label="Total" value={`${hardware.memory.total_mb.toFixed(0)} MB`} color="text-gray-200" />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex justify-between text-[8px] text-gray-200 font-bold uppercase tracking-widest">
                                        <span>Free</span>
                                        <span>{(hardware.memory.total_mb - hardware.memory.used_mb).toFixed(0)} MB</span>
                                    </div>
                                    <ProgressBar pct={hardware.memory.used_pct} color={memColor.bar} />
                                </div>
                            </div>
                            {hardware.memory.used_pct >= 85 && (
                                <div className="w-full flex items-center gap-1.5 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <ShieldAlert size={10} className="text-red-400 shrink-0" />
                                    <span className="text-[9px] text-red-400 font-bold">High usage — session drops possible</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Disk — 3 cols */}
                    <div className="col-span-12 lg:col-span-3 bg-slate-700/30 border border-slate-700/60 rounded-xl p-3">
                        <SubLabel label="Disk" />
                        {hardware.disk.partitions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center gap-2 py-8">
                                <div className="w-10 h-10 rounded-xl bg-slate-700/40 border border-slate-600/30 flex items-center justify-center">
                                    <HardDrive size={18} className="text-gray-200" />
                                </div>
                                <p className="text-[9px] font-bold text-gray-200 text-center">No local partitions</p>
                                <p className="text-[8px] text-gray-200 text-center">Firmware, logs &amp; quarantine</p>
                            </div>
                        ) : (
                            <div className="space-y-2.5">
                                {hardware.disk.partitions.map((p: any, i: number) => {
                                    const col = getPctColor(p.used_pct ?? 0);
                                    return (
                                        <div key={i} className="space-y-1">
                                            <div className="flex justify-between text-[9px]">
                                                <span className="text-gray-200 font-bold">{p.name ?? `Partition ${i + 1}`}</span>
                                                <span className={col.text}>{p.used_pct ?? 0}%</span>
                                            </div>
                                            <ProgressBar pct={p.used_pct ?? 0} color={col.bar} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* ── Row 3: HA (4) + Syslog (4) + TLS (4) ────────────────── */}
            <div className="grid grid-cols-12 gap-2 items-start">

                {/* High Availability */}
                <div className="col-span-12 lg:col-span-4 bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <CardHeader icon={<Radio className="w-4 h-4 text-cyan-400" />} label="High Availability" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Status"><BoolPill value={ha.enabled} /></Field>
                        <Field label="Mode"><span className="text-gray-200 capitalize"><Val v={ha.mode} /></span></Field>
                        <Field label="Group Name"><span className="text-gray-200"><Val v={ha.group_name || "-"} /></span></Field>
                        <Field label="Peer Count"><span className="text-gray-200">{ha.peer_count}</span></Field>
                        <Field label="Password Set"><BoolPill value={ha.password_set} trueLabel="Yes" falseLabel="No" /></Field>
                    </div>
                </div>

                {/* Syslog */}
                <div className="col-span-12 lg:col-span-4 bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="bg-slate-800 p-1.5 rounded-lg shrink-0">
                            <FileKey className="w-4 h-4 text-yellow-400" />
                        </div>
                        <h2 className="text-sm font-bold text-white">Syslog</h2>
                        <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${syslog.status === "enable"
                            ? "bg-green-500/10 border-green-500/20 text-green-400"
                            : "bg-red-500/10 border-red-500/20 text-red-400"
                            }`}>
                            {syslog.status === "enable" ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
                            {syslog.status === "enable" ? "Active" : "Inactive"}
                        </span>
                    </div>
                    <hr className="border-slate-700 mb-4" />
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Server"><span className="text-gray-200 break-all"><Val v={syslog.server || "Not configured"} /></span></Field>
                        <Field label="Port"><span className="text-gray-200">{syslog.port || "—"}</span></Field>
                        <Field label="Mode"><span className="text-gray-200 uppercase"><Val v={syslog.mode} /></span></Field>
                        <Field label="Facility"><span className="text-gray-200"><Val v={syslog.facility} /></span></Field>
                        <Field label="Format"><span className="text-gray-200"><Val v={syslog.format} /></span></Field>
                    </div>
                </div>

                {/* TLS Info */}
                <div className="col-span-12 lg:col-span-4 bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <CardHeader icon={<Key className="w-4 h-4 text-purple-400" />} label="TLS Info" />
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                            <Field label="Port"><span className="font-mono text-gray-200">{preTls.port}</span></Field>
                            <Field label="Version"><span className="font-bold text-purple-300">{preTls.tls_version}</span></Field>
                        </div>
                        <Field label="Cipher">
                            <span className="font-mono text-gray-200 break-all text-[9px]">{preTls.tls_cipher}</span>
                        </Field>
                        <Field label="Fingerprint">
                            <span className="font-mono text-gray-200 break-all text-[9px]">{preTls.tls_fingerprint_hash}</span>
                        </Field>
                    </div>
                </div>

            </div>

        </div>
    );
}
